import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import useClientService from '../../hooks/useClientService';
import useBuildingService from '../../hooks/useBuildingService';
import useDocumentService from '../../hooks/useDocumentService';
import useInvoiceService from '../../hooks/useInvoiceService';
import useCertificationService from '../../hooks/useCertificationService';
import { useSnackbar } from '../../components/SnackbarProvider';
import { Add as AddIcon } from '@mui/icons-material';
import {
    TextField,
    Button,
    Typography,
    Paper,
    Grid,
    Table,
    Box,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogContent,
    DialogActions,
    DialogTitle,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import Accordion from '../../components/Accordion';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import InvoiceRetentionTable from '../../components/InvoiceRetentionTable';
import CreatePdf from '../../components/CreatePdf';
import { formatDate, DatePickerComponent, handleApiError, itemsPerPage, IconBtn } from '../../components/Utils';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';

function EditClient() {
    const { id } = useParams();
    const navigate = useNavigate();
    const showSnackbar = useSnackbar();

    const { getClient, updateClient, disableClient } = useClientService();
    const { getBuildings, disableBuilding } = useBuildingService();
    const { getDocuments, addDocument, getDocumentBlob, disableDocument } = useDocumentService();
    const { getInvoices, createInvoice, disableInvoice } = useInvoiceService();
    const { getCertifications, disableCertification } = useCertificationService();

    // --- General loading (client form only) ---
    const [loading, setLoading] = useState({ general: true, update: false });
    const [error, setError] = useState(null);

    const [clientForm, setClientForm] = useState({
        email: null,
        name: null,
        address: null,
        doc_type: null,
        documentation: null,
        country: null,
        cp: null,
        town: null,
        commercial_name: null,
        province: null,
        telephone_number: null,
        secondary_telephone_number: null
    });

    // --- Documents section state ---
    const [documents, setDocuments] = useState([]);
    const [documentsPage, setDocumentsPage] = useState(1);
    const [documentsTotal, setDocumentsTotal] = useState(0);
    const [loadingDocuments, setLoadingDocuments] = useState(false);

    // --- Buildings section state ---
    const [buildings, setBuildings] = useState([]);
    const [buildingsPage, setBuildingsPage] = useState(1);
    const [buildingsTotal, setBuildingsTotal] = useState(0);
    const [loadingBuildings, setLoadingBuildings] = useState(false);

    // --- Certifications section state ---
    const [certifications, setCertifications] = useState([]);
    const [certificationsPage, setCertificationsPage] = useState(1);
    const [certificationsTotal, setCertificationsTotal] = useState(0);
    const [loadingCertifications, setLoadingCertifications] = useState(false);

    // --- Invoices section state ---
    const [invoices, setInvoices] = useState([]);
    const [invoicesPage, setInvoicesPage] = useState(1);
    const [invoicesTotal, setInvoicesTotal] = useState(0);
    const [loadingInvoices, setLoadingInvoices] = useState(false);
    const [totalPriceSum, setTotalPriceSum] = useState(null);
    const [paidAmountSum, setPaidAmountSum] = useState(null);
    const [remainingAmountSum, setRemainingAmountSum] = useState(null);
    const [totalRetention, setTotalRetention] = useState(null);
    const [paidRetention, setPaidRetention] = useState(null);
    const [remainingRetentionSum, setRemainingRetentionSum] = useState(null);

    // --- Upload document state ---
    const [uploadModalOpen, setUploadModalOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploadError, setUploadError] = useState(null);

    // --- Delete dialog state ---
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [deleteId, setDeleteId] = useState(false);
    const [typeDelete, setTypeDelete] = useState(false);

    // --- Invoice creation state ---
    const [openInvoiceModal, setOpenInvoiceModal] = useState(false);
    const [selectedCert, setSelectedCert] = useState(null);
    const [retention, setRetention] = useState(null);
    const [vat, setVat] = useState(null);
    const [retentionDate, setRetentionDate] = useState(null);
    const [invoiceLimitDate, setInvoiceLimitDate] = useState(null);
    const [invoiceDate, setInvoiceDate] = useState(dayjs().startOf('day').local().format());

    // -------------------------------------------------------------------------
    // Fetches
    // -------------------------------------------------------------------------

    const fetchClient = useCallback(async () => {
        try {
            setLoading(prev => ({ ...prev, general: true }));
            const result = await getClient(id);
            const client = result.data.client;
            setClientForm(client);
            setClientForm(prevState => ({ ...prevState, client_id: id }));
        } catch (error) {
            setError(handleApiError(error, 'Error obteniendo cliente.'));
        } finally {
            setLoading(prev => ({ ...prev, general: false }));
        }
    }, [id]);

    const fetchDocuments = useCallback(async () => {
        try {
            setLoadingDocuments(true);
            const result = await getDocuments({
                client_id: id,
                limit: itemsPerPage,
                offset: (documentsPage - 1) * itemsPerPage,
            });
            setDocuments(result.data.items);
            setDocumentsTotal(result.data.total);
        } catch (error) {
            showSnackbar(handleApiError(error, 'Error obteniendo documentos.'), 'error');
        } finally {
            setLoadingDocuments(false);
        }
    }, [id, documentsPage]);

    const fetchBuildings = useCallback(async () => {
        try {
            setLoadingBuildings(true);
            const result = await getBuildings({
                client_id: id,
                limit: itemsPerPage,
                offset: (buildingsPage - 1) * itemsPerPage,
            });
            setBuildings(result.data.items);
            setBuildingsTotal(result.data.total);
        } catch (error) {
            showSnackbar(handleApiError(error, 'Error obteniendo obras.'), 'error');
        } finally {
            setLoadingBuildings(false);
        }
    }, [id, buildingsPage]);

    const fetchCertifications = useCallback(async () => {
        try {
            setLoadingCertifications(true);
            const result = await getCertifications({
                client_id: id,
                limit: itemsPerPage,
                offset: (certificationsPage - 1) * itemsPerPage,
                disabled: false,
            });
            setCertifications(result.data.items);
            setCertificationsTotal(result.data.total);
        } catch (error) {
            showSnackbar(handleApiError(error, 'Error obteniendo certificaciones.'), 'error');
        } finally {
            setLoadingCertifications(false);
        }
    }, [id, certificationsPage]);

    const fetchInvoices = useCallback(async () => {
        try {
            setLoadingInvoices(true);
            const result = await getInvoices({
                client_id: id,
                only_client_invoices: true,
                limit: itemsPerPage,
                offset: (invoicesPage - 1) * itemsPerPage,
                disabled: false,
            });
            setInvoices(result.data.items);
            setInvoicesTotal(result.data.total);
            setTotalPriceSum(result.data.total_price_sum);
            setPaidAmountSum(result.data.paid_amount_sum);
            setRemainingAmountSum(result.data.remaining_amount_sum);
            setTotalRetention(result.data.total_retentions);
            setPaidRetention(result.data.paid_retention);
            setRemainingRetentionSum(result.data.remaining_retention_sum);
        } catch (error) {
            showSnackbar(handleApiError(error, 'Error obteniendo facturas.'), 'error');
        } finally {
            setLoadingInvoices(false);
        }
    }, [id, invoicesPage]);

    // -------------------------------------------------------------------------
    // Effects – one per section (fire on mount + page change)
    // -------------------------------------------------------------------------

    useEffect(() => {
        fetchClient();
    }, []);

    useEffect(() => {
        fetchDocuments();
    }, [documentsPage]);

    useEffect(() => {
        fetchBuildings();
    }, [buildingsPage]);

    useEffect(() => {
        fetchCertifications();
    }, [certificationsPage]);

    useEffect(() => {
        fetchInvoices();
    }, [invoicesPage]);

    // -------------------------------------------------------------------------
    // Handlers
    // -------------------------------------------------------------------------

    const handleChange = (e) => {
        const { name, value } = e.target;
        setClientForm({ ...clientForm, [name]: value });
    };

    const validateForm = () => {
        if (!clientForm.name) {
            return "Por favor, añada un nombre";
        }
        if (clientForm.email && clientForm.email.length > 36) {
            return "El email no debe exceder los 36 caracteres";
        }
        if (clientForm.name.length > 55) {
            return "El nombre no debe exceder los 55 caracteres";
        }
        if (clientForm.country && clientForm.country.length > 36) {
            return "El pais no debe exceder los 36 caracteres";
        }
        if (clientForm.cp && clientForm.cp.length > 9) {
            return "El codigo postal no debe exceder los 9 caracteres";
        }
        if (clientForm.town && clientForm.town.length > 150) {
            return "La comunidad autonoma no debe exceder los 150 caracteres";
        }
        if (clientForm.province && clientForm.province.length > 150) {
            return "La provincia no debe exceder los 150 caracteres";
        }
        if (clientForm.surname && clientForm.surname.length > 36) {
            return "Los apellidos no deben exceder los 36 caracteres";
        }
        if ((clientForm.doc_type && !clientForm.documentation) || (!clientForm.doc_type && clientForm.documentation)) {
            return "Si quiere poner una documentación, debe rellenar ambos campos (tipo y documentación).";
        }
        if (clientForm.documentation && clientForm.documentation.length > 15) {
            return "La documentación no debe exceder los 15 caracteres.";
        }
        if (clientForm.address && clientForm.address.length > 150) {
            return "La dirección no debe exceder los 150 caracteres";
        }
        if (clientForm.telephone_number && clientForm.telephone_number.length > 15) {
            return "El teléfono no debe exceder los 15 caracteres";
        }
        if (clientForm.secondary_telephone_number && clientForm.secondary_telephone_number.length > 15) {
            return "El teléfono secundario no debe exceder los 15 caracteres";
        }
        if (clientForm.commercial_name && clientForm.commercial_name.length > 36) {
            return "El alias no debe exceder los 36 caracteres";
        }
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        const validationError = validateForm();
        if (validationError) {
            setError(validationError);
            return;
        }
        try {
            setLoading(prev => ({ ...prev, update: true }));
            await updateClient(clientForm);
            showSnackbar('Cliente actualizado correctamente', 'success');
            fetchClient();
            setError(null);
        } catch (error) {
            const msg = handleApiError(error, 'Error al actualizar el cliente.');
            setError(msg);
            showSnackbar(msg, 'error');
        } finally {
            setLoading(prev => ({ ...prev, update: false }));
        }
    };

    const handleFileChange = (e) => {
        setSelectedFile(e.target.files[0]);
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            setUploadError("Por favor selecciona un archivo");
            return;
        }
        const formData = new FormData();
        formData.append('document', selectedFile);
        formData.append('client_id', id);

        try {
            setLoadingDocuments(true);
            await addDocument(formData);
            showSnackbar('Documento subido correctamente', 'success');
            fetchDocuments();
            setUploadModalOpen(false);
            setSelectedFile(null);
            setUploadError(null);
        } catch (error) {
            const msg = handleApiError(error, 'Error al subir el documento.');
            setUploadError(msg);
            showSnackbar(msg, 'error');
        } finally {
            setLoadingDocuments(false);
        }
    };

    const handleDownloadDocument = async (documentId, documentName) => {
        try {
            const response = await getDocumentBlob(documentId);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', documentName);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            setError(handleApiError(error, 'Error al descargar el documento.'));
        }
    };

    const handleOpenDeleteDialog = (itemId, type) => {
        setDeleteId(itemId);
        setTypeDelete(type);
        setOpenDeleteDialog(true);
    };

    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
    };

    const handleDeleteItem = async () => {
        try {
            if (typeDelete === '/certifications/disable') {
                await disableCertification(deleteId);
                fetchCertifications();
            } else if (typeDelete === '/buildings/disable') {
                await disableBuilding(deleteId);
                fetchBuildings();
            } else if (typeDelete === '/invoices/disable') {
                await disableInvoice(deleteId);
                fetchInvoices();
            } else if (typeDelete === '/clients/disable') {
                await disableClient(deleteId);
            } else if (typeDelete === '/documents/disable') {
                await disableDocument(deleteId);
                fetchDocuments();
            }

            showSnackbar('Eliminado correctamente', 'success');
            if (typeDelete === '/clients/disable') {
                navigate(`/clients/list`);
            }
            setOpenDeleteDialog(false);
        } catch (error) {
            const msg = handleApiError(error, 'Error al eliminar el elemento.');
            showSnackbar(msg, 'error');
        }
    };

    const handleClickOpenInvoice = (cert) => {
        setSelectedCert(cert);
        setOpenInvoiceModal(true);
    };

    const handleCloseInvoice = () => {
        setRetention(null);
        setRetentionDate(null);
        setOpenInvoiceModal(false);
    };

    const handleChangeRetention = (event) => {
        const { value } = event.target;
        setRetention(value);
    };

    const handleChangeVat = (event) => {
        const { value } = event.target;
        setVat(value);
    };

    const handleDateChange = (name, newValue) => {
        const date = dayjs(newValue).hour(12).minute(0).second(0).utc().format('YYYY-MM-DD');
        if (name === 'retention_limit_date') {
            setRetentionDate(date);
        } else if (name === 'invoice_date') {
            setInvoiceDate(date);
        } else if (name === 'invoice_limit_date') {
            setInvoiceLimitDate(date);
        }
    };

    const handleCreateInvoice = async () => {
        try {
            setLoadingInvoices(true);
            await createInvoice({
                certification: selectedCert.id,
                vat: vat,
                retention: retention,
                invoice_limit_date: invoiceLimitDate,
                retention_limit_date: retentionDate
            });
            showSnackbar('Factura creada correctamente', 'success');
            handleCloseInvoice();
            fetchInvoices();
        } catch (error) {
            const msg = handleApiError(error, 'Error al crear la factura.');
            showSnackbar(msg, 'error');
        } finally {
            setLoadingInvoices(false);
        }
    };

    // -------------------------------------------------------------------------
    // Render
    // -------------------------------------------------------------------------

    return (
        <div style={{ padding: 20 }}>
            {loading?.general && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                    <CircularProgress />
                </div>
            )}
            {!loading?.general && (
                <div>
                    <Grid container spacing={3} style={{ position: 'fixed', top: 60, right: 10, zIndex: 1100, width: 'auto' }}>
                        <Grid item>
                            <Paper elevation={0} style={{ padding: '10px', textAlign: 'center', backgroundColor: '#fff' }}>
                                <Box mb={2}>
                                    <Typography variant="h5" component="h1" gutterBottom>
                                        {clientForm.code} - {clientForm.name}
                                    </Typography>
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>

                    <Grid item xs={12} md={10} lg={8}>
                        <Accordion title={`${clientForm.code} ${clientForm.name}`}>
                            <form onSubmit={handleSubmit} style={{ padding: '20px' }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <Typography variant="h6">Información Principal</Typography>
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <TextField
                                            fullWidth
                                            label="Nombre"
                                            name="name"
                                            value={clientForm.name}
                                            onChange={handleChange}
                                            margin="normal"
                                            InputLabelProps={{ shrink: !!clientForm.name }}
                                            inputProps={{ maxLength: 55 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <TextField
                                            fullWidth
                                            label="Email"
                                            name="email"
                                            value={clientForm.email}
                                            onChange={handleChange}
                                            margin="normal"
                                            InputLabelProps={{ shrink: !!clientForm.email }}
                                            inputProps={{ maxLength: 36 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <TextField
                                            fullWidth
                                            label="Teléfono"
                                            name="telephone_number"
                                            value={clientForm.telephone_number}
                                            onChange={handleChange}
                                            margin="normal"
                                            InputLabelProps={{ shrink: !!clientForm.telephone_number }}
                                            inputProps={{ maxLength: 15 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <TextField
                                            fullWidth
                                            label="Teléfono secundario"
                                            name="secondary_telephone_number"
                                            value={clientForm.secondary_telephone_number}
                                            onChange={handleChange}
                                            margin="normal"
                                            InputLabelProps={{ shrink: !!clientForm.secondary_telephone_number }}
                                            inputProps={{ maxLength: 15 }}
                                        />
                                    </Grid>
                                    <Grid item xs={10} md={2}>
                                        <TextField
                                            fullWidth
                                            label="Nombre comercial (alias)"
                                            name="commercial_name"
                                            value={clientForm.commercial_name}
                                            InputLabelProps={{ shrink: !!clientForm.commercial_name }}
                                            onChange={handleChange}
                                            margin="normal"
                                            inputProps={{ maxLength: 36 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} style={{ marginTop: "20px" }}>
                                        <Typography variant="h6">Documentación</Typography>
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <FormControl
                                            style={{ width: "100%" }}
                                        >
                                            <InputLabel id="document-type-label">
                                                Documentación
                                            </InputLabel>
                                            <Select
                                                labelId="document-type-label"
                                                id="document-type"
                                                label='Tipo documentación'
                                                value={clientForm.doc_type}
                                                onChange={handleChange}
                                                name="doc_type"
                                                inputProps={{ id: 'document-type' }}
                                            >
                                                <MenuItem value="DNI">DNI</MenuItem>
                                                <MenuItem value="NIF">NIF</MenuItem>
                                                <MenuItem value="NIE">NIE</MenuItem>
                                                <MenuItem value="CIF">CIF</MenuItem>
                                                <MenuItem value="DOC_EXTRANJERA">DOC EXTRANJERA</MenuItem>
                                                <MenuItem value="OTRO">OTRO</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <TextField
                                            fullWidth
                                            label="Documentación"
                                            name="documentation"
                                            value={clientForm.documentation}
                                            onChange={handleChange}
                                            inputProps={{ maxLength: 15 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} style={{ marginTop: "20px" }}>
                                        <Typography variant="h6">Dirección</Typography>
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <TextField
                                            fullWidth
                                            label="País"
                                            name="country"
                                            value={clientForm.country}
                                            onChange={handleChange}
                                            margin="normal"
                                            InputLabelProps={{ shrink: !!clientForm.country }}
                                            inputProps={{ maxLength: 36 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <TextField
                                            fullWidth
                                            label="Población"
                                            name="town"
                                            value={clientForm.town}
                                            onChange={handleChange}
                                            margin="normal"
                                            InputLabelProps={{ shrink: !!clientForm.town }}
                                            inputProps={{ maxLength: 150 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <TextField
                                            fullWidth
                                            label="Provincia"
                                            name="province"
                                            value={clientForm.province}
                                            onChange={handleChange}
                                            margin="normal"
                                            InputLabelProps={{ shrink: !!clientForm.province }}
                                            inputProps={{ maxLength: 150 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={2}>
                                        <TextField
                                            fullWidth
                                            label="Código postal"
                                            name="cp"
                                            value={clientForm.cp}
                                            onChange={handleChange}
                                            margin="normal"
                                            InputLabelProps={{ shrink: !!clientForm.cp }}
                                            inputProps={{ maxLength: 9 }}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Dirección"
                                            name="address"
                                            value={clientForm.address}
                                            onChange={handleChange}
                                            margin="normal"
                                            InputLabelProps={{ shrink: !!clientForm.address }}
                                            inputProps={{ maxLength: 150 }}
                                        />
                                    </Grid>
                                </Grid>
                                <br />
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            onClick={() => handleOpenDeleteDialog(id, '/clients/disable')}
                                            fullWidth
                                            style={{ backgroundColor: 'red' }}>
                                            Eliminar
                                        </Button>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            color="primary"
                                            disabled={loading?.update}
                                            fullWidth>
                                            {loading?.update ? (
                                                <CircularProgress size={24} color="inherit" />
                                            ) : (
                                                'Actualizar'
                                            )}
                                        </Button>
                                    </Grid>
                                </Grid>
                                {error && (
                                    <Typography variant="body2" color="error" style={{ marginTop: 10 }}>
                                        {error}
                                    </Typography>
                                )}
                            </form>
                        </Accordion>

                        {/* Documents accordion – paginated */}
                        <Accordion
                            title="Documentos"
                            paginate
                            totalItems={documentsTotal}
                            currentPage={documentsPage}
                            onPageChange={setDocumentsPage}
                        >
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => setUploadModalOpen(true)}
                                startIcon={<AddIcon />}
                                style={{ marginTop: 10 }}
                            >
                                Subir documento
                            </Button>
                            {loadingDocuments ? (
                                <CircularProgress />
                            ) : (
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Nombre</TableCell>
                                                <TableCell>Acciones</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {documents.filter(item => !item.disabled).map(item => (
                                                <TableRow key={item.id}>
                                                    <TableCell>
                                                        <IconBtn
                                                            icon={<DownloadIcon />}
                                                            title="Descargar"
                                                            onClick={() => handleDownloadDocument(item.id, item.name)}
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <IconBtn
                                                            icon={<DeleteIcon />}
                                                            title="Eliminar"
                                                            color="error"
                                                            onClick={() => handleOpenDeleteDialog(item.id, '/documents/disable')}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}

                            <Dialog open={uploadModalOpen} onClose={() => setUploadModalOpen(false)}>
                                <DialogTitle>Subir documento (pdf, word, excel o imagen hasta 20mb)</DialogTitle>
                                <DialogContent>
                                    <input type="file" onChange={handleFileChange} />
                                    {uploadError && (
                                        <Typography variant="body2" color="error" style={{ marginTop: 10 }}>
                                            {uploadError}
                                        </Typography>
                                    )}
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={() => setUploadModalOpen(false)} color="primary">
                                        Cancelar
                                    </Button>
                                    <Button onClick={handleUpload} color="primary" variant='contained'>
                                        Enviar
                                    </Button>
                                </DialogActions>
                            </Dialog>
                        </Accordion>

                        {/* Buildings accordion – paginated */}
                        <Accordion
                            title="Obras"
                            paginate
                            totalItems={buildingsTotal}
                            currentPage={buildingsPage}
                            onPageChange={setBuildingsPage}
                        >
                            {loadingBuildings ? (
                                <CircularProgress />
                            ) : (
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Código</TableCell>
                                                <TableCell>Nombre</TableCell>
                                                <TableCell>Presupuesto</TableCell>
                                                <TableCell>Estado</TableCell>
                                                <TableCell>Acciones</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {buildings.filter(item => !item.disabled).map(item => (
                                                <TableRow key={item.id}>
                                                    <TableCell>
                                                        <Link to={`/buildings/update/${item.id}`}>
                                                            {item.code || '-'}
                                                        </Link>
                                                    </TableCell>
                                                    <TableCell>
                                                        {item.name || '-'}
                                                    </TableCell>
                                                    <TableCell>{item.budget ? item.budget.toLocaleString('es-ES', { useGrouping: true, minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '€' : '0€'}</TableCell>
                                                    <TableCell>
                                                        {item.building_status === 'not_started' ? 'NO EMPEZADA' :
                                                            item.building_status === 'in_course' ? 'EN CURSO' :
                                                                item.building_status === 'finished' ? 'TERMINADA' :
                                                                    item.offer_status || '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                        <IconBtn
                                                            icon={<DeleteIcon />}
                                                            title="Eliminar"
                                                            color="error"
                                                            onClick={() => handleOpenDeleteDialog(item.id, '/buildings/disable')}
                                                            sx={{ ml: 2 }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Accordion>

                        {/* Invoices – paginated, passed to InvoiceRetentionTable */}
                        <InvoiceRetentionTable
                            isInvoice={true}
                            isClient={true}
                            showBuilding={true}
                            showClientOrProvider={false}
                            title='Facturas'
                            totalAmount={totalPriceSum}
                            paidAmount={paidAmountSum}
                            remainingAmountSum={remainingAmountSum}
                            invoices={invoices}
                            fetch={fetchInvoices}
                            handleOpenDeleteDialog={handleOpenDeleteDialog}
                            paginate={true}
                            totalItems={invoicesTotal}
                            currentPage={invoicesPage}
                            onPageChange={setInvoicesPage}
                            exportParams={{ client_id: id, only_client_invoices: true }}
                        />
                        <InvoiceRetentionTable
                            isInvoice={false}
                            isClient={true}
                            showBuilding={true}
                            showClientOrProvider={false}
                            title='Retenciones'
                            totalAmount={totalRetention}
                            paidAmount={paidRetention}
                            remainingAmountSum={remainingRetentionSum}
                            invoices={invoices}
                            fetch={fetchInvoices}
                            handleOpenDeleteDialog={handleOpenDeleteDialog}
                            paginate={true}
                            totalItems={invoicesTotal}
                            currentPage={invoicesPage}
                            onPageChange={setInvoicesPage}
                        />

                        {/* Certifications accordion – paginated */}
                        <Accordion
                            title='Certificaciones'
                            paginate
                            totalItems={certificationsTotal}
                            currentPage={certificationsPage}
                            onPageChange={setCertificationsPage}
                        >
                            {loadingCertifications ? (
                                <CircularProgress />
                            ) : (
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Código</TableCell>
                                                <TableCell>Facturada</TableCell>
                                                <TableCell>Precio</TableCell>
                                                <TableCell>Fecha</TableCell>
                                                <TableCell>Obra</TableCell>
                                                <TableCell>Partes de la obra</TableCell>
                                                <TableCell>Acciones</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {certifications.filter(item => !item.disabled).map((item, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>
                                                        <Link to={`/certifications/update/${item.id}`}>
                                                            {item.code}
                                                        </Link>
                                                    </TableCell>
                                                    <TableCell>{item.invoiced ? "Sí" : "No"}</TableCell>
                                                    <TableCell>{item.price ? item.price.toLocaleString('es-ES', { useGrouping: true, minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '€' : '0€'}</TableCell>
                                                    <TableCell>{formatDate(item.certification_date)}</TableCell>
                                                    <TableCell>
                                                        <Link to={`/buildings/update/${item.building.id}`}>
                                                            {item.building.name || '-'}
                                                        </Link>
                                                    </TableCell>
                                                    <TableCell>
                                                        {item.item_progress.map((product, idx) => (
                                                            <div key={idx}>{product.item.name} - {product.percentage}%</div>
                                                        ))}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Button variant="contained" sx={{ marginRight: '10px' }} color="primary" disabled={item.invoiced} onClick={() => handleClickOpenInvoice(item)}>
                                                            Crear factura
                                                        </Button>
                                                        <CreatePdf
                                                            endpoint={`/certifications/${item.id}/generate-pdf`}>
                                                        </CreatePdf>
                                                        <IconBtn
                                                            icon={<DeleteIcon />}
                                                            title="Eliminar"
                                                            color="error"
                                                            onClick={() => handleOpenDeleteDialog(item.id, '/certifications/disable')}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Accordion>

                        {selectedCert && (
                            <Dialog open={openInvoiceModal} onClose={handleCloseInvoice}>
                                <DialogTitle>Crear Factura</DialogTitle>
                                <DialogContent>
                                    <br />
                                    <Grid>
                                        <DatePickerComponent
                                            label="Fecha factura"
                                            value={invoiceDate}
                                            onChange={(newValue) => handleDateChange('invoice_date', newValue)}
                                        />
                                    </Grid>
                                    <br />
                                    <Grid>
                                        <DatePickerComponent
                                            label="Fecha vencimiento factura"
                                            value={invoiceLimitDate}
                                            onChange={(newValue) => handleDateChange('invoice_limit_date', newValue)}
                                        />
                                    </Grid>
                                    <Grid>
                                        <TextField
                                            name="vat"
                                            label="Iva"
                                            fullWidth
                                            value={vat}
                                            onChange={handleChangeVat}
                                            margin="normal"
                                            type="number"
                                            inputProps={{
                                                min: "0",
                                                max: "100",
                                                step: "1"
                                            }}
                                        />
                                    </Grid>
                                    <Grid>
                                        <TextField
                                            name="retention"
                                            label="Retención (porcentaje)"
                                            fullWidth
                                            value={retention}
                                            onChange={handleChangeRetention}
                                            margin="normal"
                                            type="number"
                                            inputProps={{
                                                min: "0",
                                                max: "100",
                                                step: "1"
                                            }}
                                        />
                                    </Grid>
                                    <br />
                                    <Grid>
                                        <DatePickerComponent
                                            label="Fecha vencimiento retención"
                                            value={retentionDate}
                                            onChange={(newValue) => handleDateChange('retention_limit_date', newValue)}
                                        />
                                    </Grid>
                                    <br />
                                </DialogContent>
                                <DialogActions>
                                    <Button onClick={handleCloseInvoice} color="primary">
                                        Cancelar
                                    </Button>
                                    <Button color="primary" disabled={!retention || !retentionDate} onClick={handleCreateInvoice}>
                                        Crear Factura
                                    </Button>
                                </DialogActions>
                            </Dialog>
                        )}

                        <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
                            <DialogTitle>¿Estás seguro?</DialogTitle>
                            <DialogActions>
                                <Button onClick={handleCloseDeleteDialog} color="primary">
                                    Volver
                                </Button>
                                <Button onClick={handleDeleteItem} color="error">
                                    Eliminar
                                </Button>
                            </DialogActions>
                        </Dialog>
                    </Grid>
                </div>
            )}
        </div>
    );
}

export default EditClient;
